import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useVatReports, useGenerateVatReport, useExport } from '@/hooks/useApi';
import toast from 'react-hot-toast';
import PageSkeleton from '@/components/PageSkeleton';

export default function VatReportsPage() {
  const { t } = useTranslation();
  const { data: reports, isLoading } = useVatReports();
  const generateReport = useGenerateVatReport();
  const exportMutation = useExport();

  const [showGenerate, setShowGenerate] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [selectedReport, setSelectedReport] = useState<any>(null);

  function handleGenerate(e: React.FormEvent) {
    e.preventDefault();
    const periodStart = `${year}-${String((quarter - 1) * 3 + 1).padStart(2, '0')}-01`;
    const lastMonth = quarter * 3;
    const lastDay = new Date(year, lastMonth, 0).getDate();
    const periodEnd = `${year}-${String(lastMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
    generateReport.mutate(
      { periodStart, periodEnd, periodType: 'quarterly' },
      {
        onSuccess: () => { toast.success(t('reports.generated')); setShowGenerate(false); },
      }
    );
  }

  const GRID_LABELS: Record<string, string> = {
    '00': 'Goods & services at 0%', '01': 'Goods & services at 6%', '02': 'Goods & services at 12%',
    '03': 'Goods & services at 21%', '44': 'Services (intra-community)', '45': 'Goods (intra-community)',
    '46': 'Exempt & non-EU', '47': 'Credit notes issued', '48': 'Credit notes received',
    '49': 'Credit notes (intra-community)', '54': 'VAT on 01', '55': 'VAT on 02', '56': 'VAT on 03',
    '57': 'VAT on imports', '59': 'Input VAT deductible', '61': 'Various regularizations (due)',
    '62': 'Various regularizations (deductible)', '63': 'VAT on credit notes received',
    '64': 'VAT on credit notes issued', '71': 'VAT due to State', '72': 'VAT credits',
    XX: 'Total',
  };

  if (isLoading) return <PageSkeleton variant="grid" />;

  return (
    <div>
      <div className="page-header">
        <h1 className="text-2xl font-bold">{t('reports.title')}</h1>
        <button className="btn btn-primary" onClick={() => setShowGenerate(true)}>
          + {t('reports.generateReport')}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Reports list */}
        <div className="space-y-3">
          {isLoading && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="card card-body"><div className="skeleton h-4 w-32 mb-2" /><div className="skeleton h-4 w-20" /></div>
          ))}
          {reports?.map((r) => (
            <button
              key={r.id}
              onClick={() => setSelectedReport(r)}
              className={`card card-body text-left w-full transition ${selectedReport?.id === r.id ? 'ring-2 ring-primary-500' : 'hover:border-gray-300'}`}
            >
              <p className="font-semibold">Q{Math.ceil((new Date(r.periodStart).getMonth() + 1) / 3)} {new Date(r.periodStart).getFullYear()}</p>
              <p className="text-sm text-gray-500">{r.periodStart} → {r.periodEnd}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-gray-500">{t('reports.vatDue')}</span>
                <span className="font-medium">€{Number(r.vatDue || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('reports.vatDeductible')}</span>
                <span className="font-medium">€{Number(r.vatDeductible || 0).toFixed(2)}</span>
              </div>
            </button>
          ))}
          {!isLoading && (!reports || reports.length === 0) && (
            <div className="text-center text-gray-400 py-8">{t('common.noResults')}</div>
          )}
        </div>

        {/* Report detail */}
        <div className="lg:col-span-2">
          {selectedReport ? (
            <div className="card">
              <div className="card-header flex items-center justify-between">
                <h3 className="font-semibold">
                  {t('reports.vatDeclaration')} – Q{Math.ceil((new Date(selectedReport.periodStart).getMonth() + 1) / 3)} {new Date(selectedReport.periodStart).getFullYear()}
                </h3>
                <button className="btn btn-secondary btn-sm" onClick={() => exportMutation.mutate({ type: 'vat-report', params: { periodStart: selectedReport.periodStart, periodEnd: selectedReport.periodEnd } })}>
                  {t('common.export')}
                </button>
              </div>
              <div className="card-body p-0">
                <table className="table">
                  <thead>
                    <tr>
                      <th className="w-16">{t('reports.grid')}</th>
                      <th>{t('reports.description')}</th>
                      <th className="text-right">{t('common.amount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {selectedReport.gridDetails && Object.entries(selectedReport.gridDetails)
                      .filter(([, v]) => Number(v) !== 0)
                      .map(([code, value]) => (
                        <tr key={code} className={code === 'XX' || code === '71' || code === '72' ? 'bg-gray-50 font-semibold' : ''}>
                          <td className="font-mono text-sm">[{code}]</td>
                          <td className="text-gray-700">{GRID_LABELS[code] || code}</td>
                          <td className="text-right font-medium">€{Number(value).toLocaleString('de-BE', { minimumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="card-body border-t">
                <div className="flex justify-between text-lg">
                  <span className="font-bold">{t('reports.balance')}</span>
                  <span className={`font-bold ${Number(selectedReport.vatDue) - Number(selectedReport.vatDeductible) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    €{(Number(selectedReport.vatDue || 0) - Number(selectedReport.vatDeductible || 0)).toLocaleString('de-BE', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-400 py-20">
              {t('reports.selectReport')}
            </div>
          )}
        </div>
      </div>

      {/* Generate modal */}
      {showGenerate && (
        <div className="modal-backdrop">
          <form onSubmit={handleGenerate} className="card card-body w-full max-w-sm space-y-4 modal-sheet">
            <h2 className="text-lg font-bold">{t('reports.generateReport')}</h2>
            <div>
              <label className="label">{t('reports.year')}</label>
              <input type="number" className="input" value={year} onChange={(e) => setYear(Number(e.target.value))} required />
            </div>
            <div>
              <label className="label">{t('reports.quarter')}</label>
              <select className="input" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                <option value={1}>Q1 (Jan–Mar)</option>
                <option value={2}>Q2 (Apr–Jun)</option>
                <option value={3}>Q3 (Jul–Sep)</option>
                <option value={4}>Q4 (Oct–Dec)</option>
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="btn btn-secondary" onClick={() => setShowGenerate(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" disabled={generateReport.isPending}>{t('reports.generate')}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
